<template>
  <v-row no-gutters style="position: relative;">

    <debt-add 
      v-if="household.id" 
      :open="addDebtDialog" 
      :household-id="household.id"
      :debt-id="addDebtEditId"
      title="Wealth Transfer"
      @on-save="updateDebt"
      @on-close="addDebtDialog = false"
    />

    <v-col cols="12" style="position: relative;">
      
      <v-row no-gutters>
        <v-col 
          cols="12"
        >
          <v-row no-gutters class="flex-column">
            
            <v-col class="mb-4">
              <div class="mb-2 text-right">
                <v-btn color="primary" @click="$router.push({name: 'households-view', params: {household_id: householdId}})">Skip Intake Form</v-btn>
              </div>
              <v-card>

                <v-fade-transition mode="out-in">
                  <v-card-text 
                    class="pa-0"
                  >
                    <div v-if="loading" class="text-center pa-6">
                      <v-progress-circular
                        :size="40"
                        :width="7"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                    <div v-else>
                      <v-container fluid class="py-0">
                        <v-row>
                          <v-col cols="6">
                            <v-row>
                              <v-col cols="12" class="py-0">
                                <div class="title primary--text">Homeowner</div>
                              </v-col>
                              <v-col cols="8">
                                <user-search-field label="Name" 
                                  :user.sync="newUser" 
                                  v-if="household.user"
                                  :agent-id="(household.user.agent_id || null)"
                                />
                              </v-col>
                              <v-col>
                                <v-text-field
                                  v-model="newUserIncome"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Takehome Income"
                                />
                              </v-col>
                            </v-row>
                          </v-col>

                          <v-col cols="6">
                            <v-row>
                              <v-col cols="12" class="py-0">
                                <div class="title">Spouse</div>
                              </v-col>
                              <v-col cols="8">
                                <user-search-field 
                                  label="Name" 
                                  :user.sync="newSpouse" 
                                  clearable
                                  @on-user-added="newSpouseIncome = $event.income"
                                  v-if="household.user"
                                  :agent-id="(household.user.agent_id || null)"
                                />
                              </v-col>
                              <v-col>
                                <v-text-field
                                  v-model="newSpouseIncome"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Takehome Income"
                                />
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">Passive Income</div>
                          </v-col>

                          <v-col cols="12" class="py-0">
                            <v-row 
                              v-for="(income,incomeIndex) in incomes"
                              :key="'income-' + incomeIndex"
                            >
                              <v-col cols="3" class="py-1">
                                <v-text-field
                                  v-model="income.name"
                                  outlined
                                  dense
                                  hide-details
                                  label="Name"
                                />
                              </v-col>
                              <v-col cols="3" class="py-1">
                                <v-text-field
                                  v-model="income.amount"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Amount ($)"
                                />
                              </v-col>
                              <v-col cols="3" class="py-1">
                                <v-text-field
                                  v-model="income.increase"
                                  outlined
                                  dense
                                  hide-details
                                  label="Annual Increase ($)"
                                />
                              </v-col>
                              <v-col cols="3" class="py-1">
                                <v-text-field
                                  v-model="income.until"
                                  outlined
                                  dense
                                  hide-details
                                  label="Continues Until"
                                  :append-outer-icon="(incomes.length > 1 ? 'mdi-close' : null)"
                                  @click:append-outer="removeIncome(incomeIndex)"
                                />
                              </v-col>
                            </v-row>
                          </v-col>

                          <v-col cols="12" class="text-center">
                            <v-btn small color="primary" @click="addIncome"><v-icon small>mdi-plus</v-icon> Add Income</v-btn>
                          </v-col>
                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">Wealth Transfers</div>
                          </v-col>

                          <v-col
                            cols="12"
                            v-for="debt in household.debts"
                            :key="'household-debt-' + debt.id"
                            class="py-1"
                          >
                            <debt-container :debt="debt" deletable @on-delete="deleteDebt(debt)" editable @on-edit="editDebt(debt)" />
                          </v-col>

                          <v-col cols="12" class="text-center">
                            <v-btn small color="primary" @click="newDebt"><v-icon small>mdi-plus</v-icon> Add Wealth Transfer</v-btn>
                          </v-col>
                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">Current Life Insurance</div>
                          </v-col>
                          <v-col
                            cols="12"
                            v-for="(iAccount, iIndex) in insurances"
                            :key="'insurance-debt-' + iIndex"
                            class="py-1"
                          >
                            <v-row>
                              <v-col cols="2" class="py-0">
                                <v-select
                                  v-model="iAccount.owner"
                                  :items="ownerSelections"
                                  hide-details
                                  outlined
                                  dense
                                  placeholder="Choose..."
                                  label="Owner"
                                  clearable
                                />
                              </v-col>
                              <v-col class="py-0" style="width: 240px; max-width: 240px; min-width: 240px;">
                                <v-select
                                  v-model="iAccount.type"
                                  :items="[{text: 'Term Insurance', value: 'term'},{text: 'Cash Insurance', value: 'cash'}]"
                                  hide-details
                                  outlined
                                  dense
                                  label="What Type"
                                  placeholder="Choose"
                                  clearable
                                />
                              </v-col>
                              <v-col v-if="iAccount.type && iAccount.owner" class="py-0">
                                <v-text-field
                                  v-model="iAccount.company"
                                  hide-details
                                  outlined
                                  dense
                                  clearable
                                  label="Company"
                                />
                              </v-col>
                              <v-col v-if="iAccount.type && iAccount.owner" class="py-0">
                                <v-text-field
                                  v-model="iAccount.death_benefit"
                                  hide-details
                                  outlined
                                  dense
                                  clearable
                                  label="Death Benefit"
                                />
                              </v-col>
                              <v-col  v-if="iAccount.type && iAccount.owner" class="py-0">
                                <v-text-field
                                  v-model="iAccount.monthly_premium"
                                  hide-details
                                  outlined
                                  dense
                                  clearable
                                  label="Monthly Premium"
                                />
                              </v-col>
                              
                              <v-col v-if="iAccount.type == 'term' && iAccount.owner" class="py-0">
                                <v-text-field
                                  v-model="iAccount.term_left"
                                  hide-details
                                  outlined
                                  dense
                                  clearable
                                  label="Term Left"
                                />
                              </v-col>
                              <v-col v-if="iAccount.type == 'cash' && iAccount.owner" class="py-0">
                                <v-text-field
                                  v-model="iAccount.cash_value"
                                  hide-details
                                  outlined
                                  dense
                                  clearable
                                  label="Cash Value"
                                />
                              </v-col>
                              <v-spacer v-if="insurances.length > 1 && (!iAccount.owner || !iAccount.type)" />
                              <v-col class="py-0 flex-grow-0 flex-shrink-1" v-if="insurances.length > 1">
                                <v-btn text style="min-width: 0px;" class="px-3" @click="removeInsurance(iIndex)"><v-icon>mdi-close</v-icon></v-btn>
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-col cols="12" class="text-center">
                            <v-btn small color="primary" @click="addInsurance"><v-icon small>mdi-plus</v-icon> Add Insurance</v-btn>
                          </v-col>
                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">Liquid Assets</div>
                          </v-col>

                          <v-col
                            cols="12"
                            v-for="(sAccount, sIndex) in savings"
                            :key="'savings-account-' + sIndex"
                            class="py-1"
                          >
                            <v-row class="align-center">
                              <v-col cols="2" class="py-0">
                                <v-select
                                  v-model="sAccount.owner"
                                  :items="ownerSelections"
                                  hide-details
                                  outlined
                                  dense
                                  placeholder="Choose..."
                                  label="Owner"
                                  clearable
                                />
                              </v-col>
                              <v-col class="py-0" v-if="sAccount.owner" style="width: 240px; max-width: 240px; min-width: 240px;">
                                <v-select
                                  v-model="sAccount.type"
                                  :items="savingsSelections"
                                  hide-details
                                  outlined
                                  dense
                                  placeholder="Choose..."
                                  label="Type"
                                  clearable
                                />
                              </v-col>
                              <v-col class="py-0" v-if="sAccount.owner && sAccount.type" style="width: 160px; max-width: 160px; min-width: 160px;">
                                <v-text-field
                                  v-model="sAccount.current_value"
                                  outlined
                                  dense
                                  hide-details
                                  label="Current Value"
                                />
                              </v-col>
                              <v-col cols="2" class="py-0" v-if="sAccount.owner && sAccount.type && sAccount.type == 'savings'">
                                <v-text-field
                                  v-model="sAccount.monthly_contribution"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Contribution"
                                />
                              </v-col>
                              <v-spacer v-if="savings.length > 1" />
                              <v-col class="py-0 flex-grow-0 flex-shrink-1" v-if="savings.length > 1">
                                <v-btn text style="min-width: 0px;" class="px-3" @click="removeSavings(sIndex)"><v-icon>mdi-close</v-icon></v-btn>
                              </v-col>
                            </v-row>
                          </v-col>
                          <!--<v-col cols="12" class="py-0">
                            <v-row>
                              <v-col cols="3" class="py-0">
                                <v-checkbox
                                  v-model="userHasSavings"
                                  class="mt-0"
                                  :label="newUser.first_name + ' has a savings account'"
                                  dense
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="userHasSavings">
                                <v-text-field
                                  v-model="userSavingsCumulative"
                                  outlined
                                  dense
                                  hide-details
                                  label="Savings Cumulative Value"
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="userHasSavings">
                                <v-text-field
                                  v-model="userSavingsContribution"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Contribution"
                                />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col cols="3" class="py-0">
                                <v-checkbox
                                  v-model="userHasCds"
                                  class="mt-0"
                                  :label="newUser.first_name + ' has a C.D'"
                                  dense
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="userHasCds">
                                <v-text-field
                                  v-model="userCdsCumulative"
                                  outlined
                                  dense
                                  hide-details
                                  label="C.D.s Cumulative Value"
                                />
                              </v-col>
                            </v-row>
                            <v-row v-if="newSpouse">
                              <v-col cols="3" class="py-0">
                                <v-checkbox
                                  v-model="spouseHasSavings"
                                  class="mt-0"
                                  :label="newSpouse.first_name + ' has a savings account'"
                                  dense
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="spouseHasSavings">
                                <v-text-field
                                  v-model="spouseSavingsCumulative"
                                  outlined
                                  dense
                                  hide-details
                                  label="Savings Cumulative Value"
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="spouseHasSavings">
                                <v-text-field
                                  v-model="spouseSavingsContribution"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Contribution"
                                />
                              </v-col>
                            </v-row>
                            <v-row v-if="newSpouse">
                              <v-col cols="3" class="py-0">
                                <v-checkbox
                                  v-model="spouseHasCds"
                                  class="mt-0"
                                  :label="newSpouse.first_name + ' has a C.Ds'"
                                  dense
                                />
                              </v-col>
                              <v-col cols="3" class="py-0" v-if="spouseHasCds">
                                <v-text-field
                                  v-model="spouseCdsCumulative"
                                  outlined
                                  dense
                                  hide-details
                                  label="C.D.s Cumulative Value"
                                />
                              </v-col>
                            </v-row>
                          </v-col>-->
                          
                          <v-col cols="12" class="text-center">
                            <v-btn small color="primary" @click="addSavings"><v-icon small>mdi-plus</v-icon> Add Savings Account</v-btn>
                          </v-col>

                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">Retirement Accounts</div>
                          </v-col>

                          <v-col
                            cols="12"
                            v-for="(rAccount, rIndex) in retirements"
                            :key="'retirement-debt-' + rIndex"
                            class="py-1"
                          >
                            <v-row class="align-center">
                              <v-col cols="2" class="py-0">
                                <v-select
                                  v-model="rAccount.owner"
                                  :items="ownerSelections"
                                  hide-details
                                  outlined
                                  dense
                                  placeholder="Choose..."
                                  label="Owner"
                                  clearable
                                />
                              </v-col>
                              <v-col class="py-0" v-if="rAccount.owner" style="width: 240px; max-width: 240px; min-width: 240px;">
                                <v-select
                                  v-model="rAccount.type"
                                  :items="retirementSelections"
                                  hide-details
                                  outlined
                                  dense
                                  placeholder="Choose..."
                                  label="Type"
                                  clearable
                                />
                              </v-col>
                              <v-col class="py-0" v-if="rAccount.owner && rAccount.type" style="width: 160px; max-width: 160px; min-width: 160px;">
                                <v-text-field
                                  v-model="rAccount.current_value"
                                  outlined
                                  dense
                                  hide-details
                                  label="Current Value"
                                />
                              </v-col>
                              <v-col cols="2" class="py-0" v-if="rAccount.owner && rAccount.type && rAccount.type != 'old401k'">
                                <v-text-field
                                  v-model="rAccount.monthly_contribution"
                                  outlined
                                  dense
                                  hide-details
                                  label="Monthly Contribution"
                                />
                              </v-col>
                              <v-col cols="2" class="py-0" v-if="rAccount.owner && rAccount.type == '401k'">
                                <v-text-field
                                  outlined
                                  v-model="rAccount.employer_matchback_amount"
                                  label="Employer Contribution"
                                  dense
                                  hide-details
                                />
                              </v-col>
                              <v-spacer v-if="retirements.length > 1 && (rAccount.type != '401k' || !rAccount.employer_matchback)" />
                              <v-col class="py-0 flex-grow-0 flex-shrink-1" v-if="retirements.length > 1">
                                <v-btn text style="min-width: 0px;" class="px-3" @click="removeRetirement(rIndex)"><v-icon>mdi-close</v-icon></v-btn>
                              </v-col>
                            </v-row>
                          </v-col>

                          <v-col cols="12" class="text-center">
                            <v-btn small color="primary" @click="addRetirement"><v-icon small>mdi-plus</v-icon> Add Retirement Account</v-btn>
                          </v-col>
                        </v-row>

                        <v-divider />

                        <v-row>
                          <v-col cols="12">
                            <div class="title primary--text">What's Next</div>
                          </v-col>
                        </v-row>
                        <v-row class="flex-nowrap">
                          <v-col class="text-no-wrap flex-shrink-1 flex-grow-0">
                            <ol>
                              <li value="1">Are you anticipating any major lifestyle changes?<br>(ie., marriage, divorce, retirement, moving etc.)
                              </li>
                            </ol>
                          </v-col>
                          <v-col>
                            <v-radio-group row v-model="lifestyleChanges" dense hide-details class="mt-0">
                              <v-radio
                                label="Yes"
                                value="yes"
                              ></v-radio>
                              <v-radio
                                label="No"
                                value="no"
                              ></v-radio>
                              <v-radio
                                label="Uncertain"
                                value="uncertain"
                              ></v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row v-if="lifestyleChanges == 'yes'">
                          <v-col cols="12" class="pt-0 pl-9">
                            <v-textarea
                              v-model="lifestyleChangeReason"
                              outlined
                              label="What changes are you expecting?"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                        <v-row class="flex-nowrap align-center">
                          <v-col class="text-no-wrap flex-shrink-1 flex-grow-0">
                            <ol>
                              <li value="2">Are you happy with your current cash flow?
                              </li>
                            </ol>
                          </v-col>
                          <v-col>
                            <v-radio-group row v-model="happyCurrentCashflow" dense hide-details class="mt-0">
                              <v-radio
                                label="Yes"
                                value="yes"
                              ></v-radio>
                              <v-radio
                                label="No"
                                value="no"
                              ></v-radio>
                              <v-radio
                                label="Uncertain"
                                value="uncertain"
                              ></v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row v-if="happyCurrentCashflow == 'no'">
                          <v-col cols="12" class="pt-0 pl-9">
                            <v-textarea
                              v-model="whyNotHappy"
                              outlined
                              label="Why not?"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                        <v-row class="flex-nowrap align-center">
                          <v-col class="text-no-wrap flex-shrink-1 flex-grow-0">
                            <ol>
                              <li value="3">
                                Do you anticipate any significant changes in your cash flow?
                              </li>
                            </ol>
                          </v-col>
                          <v-col>
                            <v-radio-group row v-model="anticipateCashflowChanges" dense hide-details class="mt-0">
                              <v-radio
                                label="Yes"
                                value="yes"
                              ></v-radio>
                              <v-radio
                                label="No"
                                value="no"
                              ></v-radio>
                              <v-radio
                                label="Uncertain"
                                value="uncertain"
                              ></v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row v-if="anticipateCashflowChanges == 'yes'">
                          <v-col cols="12" class="pt-0 pl-9">
                            <v-textarea
                              v-model="cashflowChanges"
                              outlined
                              label="What changes are you expecting?"
                              hide-details
                            />
                          </v-col>
                        </v-row>
                        <v-row class="flex-nowrap align-center">
                          <v-col class="text-no-wrap flex-shrink-1 flex-grow-0">
                            <ol>
                              <li value="4">
                                Do you anticipate any major expenditures in the near future?
                              </li>
                            </ol>
                          </v-col>
                          <v-col>
                            <v-radio-group row v-model="anticipateMajorExpenditures" dense hide-details class="mt-0">
                              <v-radio
                                label="Yes"
                                value="yes"
                              ></v-radio>
                              <v-radio
                                label="No"
                                value="no"
                              ></v-radio>
                              <v-radio
                                label="Uncertain"
                                value="uncertain"
                              ></v-radio>
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-row v-if="anticipateMajorExpenditures == 'yes'">
                          <v-col cols="12" class="pt-0 pl-9">
                            <v-textarea
                              v-model="majorExpenditure"
                              outlined
                              label="What expenditures?"
                              hide-details
                            />
                          </v-col>
                        </v-row>

                      </v-container>
                    </div>

                    <div class="text-center py-6">
                      <v-btn color="primary" @click="saveHousehold" :loading="savingHousehold" :disabled="loading">Save & Continue</v-btn>
                    </div>
                  </v-card-text>
                </v-fade-transition>
              </v-card>
            </v-col>
          
          </v-row>
        </v-col>
      </v-row>
    </v-col>

  </v-row>
</template>

<script>
import UserSearchField from '@/components/UserSearchField'
import DebtAdd from '@/views/households/liabilities/DebtAdd'
import DebtContainer from '@/components/DebtContainer'

export default {
  name: 'HouseholdsIntake',
  data: () => ({
    loading: true,
    savingHousehold: false,
    household: {},
    addDebtDialog: false,
    addDebtEditId: null,

    newUser: null,
    newUserIncome: null,
    newSpouse: null,
    newSpouseIncome: null,

    userHasSavings: false,
    userSavingsCumulative: null,
    userSavingsContribution: null,
    userHasCds: false,
    userCdsCumulative: null,

    spouseHasSavings: false,
    spouseSavingsCumulative: null,
    spouseSavingsContribution: null,
    spouseHasCds: false,
    spouseCdsCumulative: null,

    lifestyleChanges: null,
    lifestyleChangeReason: null,
    happyCurrentCashflow: null,
    whyNotHappy: null,
    anticipateCashflowChanges: null,
    cashflowChanges: null,
    anticipateMajorExpenditures: null,
    majorExpenditure: null,

    incomes: [],
    insurances: [],
    retirements: [],
    savings: [],

    expansions: 'user,spouse,debts'
  }),
  components: {
    UserSearchField,
    DebtAdd,
    DebtContainer
  },
  watch: {
    householdId(newVal) {
      if(newVal) this.getDataFromApi()
    }
  },
  mounted() {
    this.getDataFromApi()
  },
  computed: {
    householdId() {
      return this.$route.params.household_id
    },
    ownerSelections() {
      let arr = []
      if(this.newUser) arr.push({text: this.newUser.full_name, value: 'user'});
      if(this.newSpouse) arr.push({text: this.newSpouse.full_name, value: 'spouse'});
      return arr
    },
    retirementSelections() {
      return [
        {
          text: 'Active 401(k)',
          value: '401k'
        },
        {
          text: 'Old 401(k)',
          value: 'old401k'
        },
        {
          text: 'IRA',
          value: 'ira'
        },
        {
          text: 'Roth IRA',
          value: 'roth'
        }
      ]
    },
    savingsSelections() {
      return [
        {
          text: 'Savings Account',
          value: 'savings'
        },
        {
          text: 'C.D.',
          value: 'cd'
        }
      ]
    }
  },
  methods: {
    parseApiData (data) {
      this.household                          = data
      this.newUser                            = data.user
      this.newSpouse                          = data.spouse
      this.newUserIncome                      = data.user_income_monthly
      this.newSpouseIncome                    = data.spouse_income_monthly

      if(data.intake.incomes) {
        this.incomes = data.intake.incomes
      } else {
        this.addIncome()
      }
      if(data.intake.retirements) {
        this.retirements = data.intake.retirements
      } else {
        this.addRetirement()
      }
      if(data.intake.insurances) {
        this.insurances = data.intake.insurances
      } else {
        this.addInsurance()
      }
      if(data.intake.savings) {
        this.savings = data.intake.savings
      } else {
        this.addSavings()
      }
      
      this.lifestyleChanges                   = data.intake.lifestyleChanges
      this.lifestyleChangeReason              = data.intake.lifestyleChangeReason
      this.happyCurrentCashflow               = data.intake.happyCurrentCashflow
      this.whyNotHappy                        = data.intake.whyNotHappy
      this.anticipateCashflowChanges          = data.intake.anticipateCashflowChanges,
      this.cashflowChanges                    = data.intake.cashflowChanges,
      this.anticipateMajorExpenditures        = data.intake.anticipateMajorExpenditures,
      this.majorExpenditure                   = data.intake.majorExpenditure
    },
    getDataFromApi() {
      this.loading = true;
      this.$http.get('households/' + this.householdId + '?expand=' + this.expansions)
        .then(response => {
          this.parseApiData(response.data)
        })
        .catch(err => {
          console.log(err.response)
          this.$swal.fire('Error', 'There was an error loading Household ID ' + this.householdId, 'error')
          this.$router.go(-1)
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveHousehold() {
      if(!this.newUser || !this.newUser.id) {
        this.$swal.fire('Homeowner Required', 'A Homeowner is required and can not be blank', 'error')
        return
      }
      this.savingHousehold = true

      let uIncome = (this.newUserIncome ? this.newUserIncome : 0)
      let sIncome = (this.newSpouse && this.newSpouse.id ? this.newSpouseIncome : 0)
      if(!sIncome) sIncome = 0

      for(let i = 0; i < this.retirements.length; i++) {
        if(!this.retirements[i].id) this.retirements[i].id = Date.now() + '-' + Math.floor(Math.random() * 100000000)
      }
      for(let i = 0; i < this.insurances.length; i++) {
        if(!this.insurances[i].id) this.insurances[i].id = Date.now() + '-' + Math.floor(Math.random() * 100000000)
      }
      for(let i = 0; i < this.savings.length; i++) {
        if(!this.savings[i].id) this.savings[i].id = Date.now() + '-' + Math.floor(Math.random() * 100000000)
      }

      let data = {
        spouse_id: (this.newSpouse ? this.newSpouse.id : null),
        spouse_income_monthly: sIncome,
        user_id: this.newUser.id,
        user_income_monthly: uIncome,
        intake: {
          incomes: this.incomes,
          retirements: this.retirements,
          insurances: this.insurances,
          savings: this.savings,
          lifestyleChanges: this.lifestyleChanges,
          lifestyleChangeReason: this.lifestyleChangeReason,
          happyCurrentCashflow: this.happyCurrentCashflow,
          whyNotHappy: this.whyNotHappy,
          anticipateCashflowChanges: this.anticipateCashflowChanges,
          cashflowChanges: this.cashflowChanges,
          anticipateMajorExpenditures: this.anticipateMajorExpenditures,
          majorExpenditure: this.majorExpenditure
        }
      }
      this.$http.put('households/' + this.householdId + '?expand=' + this.expansions, data)
        .then(response => {
          this.parseApiData(response.data)
          this.$router.push({name: 'households-view', params: {household_id: this.householdId}, query: {showSummary:1}})
        })
        .catch(err => {
          console.log(err.response)
          this.$swal.fire('Error', 'There wasn an error updating the household', 'error')
        })
        .finally(() => {
          this.savingHousehold = false
        })
    },
    addIncome() {
      this.incomes.push({
        name: null,
        amount: null,
        increase: null,
        until: null
      })
    },
    removeIncome(index) {
      this.incomes.splice(index, 1)
    },
    addRetirement() {
      this.retirements.push({
        owner: null,
        type: null,
        current_value: null,
        monthly_contribution: null,
        employer_matchback_amount: null
      })
    },
    removeRetirement(index) {
      this.retirements.splice(index, 1)
    },
    addInsurance() {
      this.insurances.push({
        owner: null,
        type: null,
        company: null,
        death_benefit: null,
        monthly_premium: null,
        term_left: null,
        cash_value: null
      })
    },
    removeInsurance(index) {
      this.insurances.splice(index, 1)
    },
    addSavings () {
      this.savings.push({
        owner: null,
        type: null,
        current_value: null,
        monthly_contribution: null
      })
    },
    removeSavings (index) {
      this.savings.splice(index, 1)
    },

    updateDebt ( debt ) {
      this.addDebtDialog = false

      let found = null
      for(let i = 0; i < this.household.debts.length; i++) {
        if(this.household.debts[i].id == debt.id) {
          found = i
          break
        }
      }

      if(found !== null) {
        this.$set(this.household.debts, found, debt)
      } else {
        this.household.debts.push(debt)
      }
    },

    newDebt () {
      this.addDebtEditId = null
      this.addDebtDialog = true
    },
    deleteDebt ( debt ) {
      this.$swal.fire({
        title: 'Are you sure?',
        html: 'Please confirm that you want to remove<br><b>' + debt.name + ' - $' + parseFloat(debt.balance).toLocaleString(undefined, {maximumFractionDigits:2}) + '</b>',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showLoaderOnConfirm: true,
        showCancelButton: true,
        preConfirm: () => {
          this.$swal.update({showCancelButton: false});
          this.$http.delete('debts/' + debt.id)
            .finally(() => {
              for(let i = 0; i < this.household.debts.length; i++) {
                if(this.household.debts[i].id == debt.id) {
                  this.household.debts.splice(i, 1)
                  break
                }
              }
              return {}
            })
        }
      })
    },
    editDebt ( debt ) {
      this.addDebtEditId  = debt.id
      this.addDebtDialog  = true
    }
  }
}
</script>